import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Pencil,
  Delete,
  ChevronUp,
  ChevronDown,
  User,
  RefreshCw,
  Upload
} from 'lucide-react';
import toast from 'react-hot-toast';
import { receptoresApi } from '../../utils/api';

// Toolbar button component
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

const TIPOS_IDENTIFICACION = {
  '01': 'Cédula Física',
  '02': 'Cédula Jurídica',
  '03': 'DIMEX',
  '04': 'NITE',
  '05': 'Extranjero'
};

const PROVINCIAS = {
  '1': 'San José',
  '2': 'Alajuela',
  '3': 'Cartago',
  '4': 'Heredia',
  '5': 'Guanacaste',
  '6': 'Puntarenas',
  '7': 'Limón'
};

export default function ListaClientes() {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [sortField, setSortField] = useState('nombre');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(1);
  const [totalClientes, setTotalClientes] = useState(0);
  const PAGE_SIZE = 30;

  useEffect(() => {
    cargarClientes();
  }, [page]);

  const cargarClientes = async (search = '') => {
    setLoading(true);
    try {
      const response = await receptoresApi.list({ q: search, limit: PAGE_SIZE, page });
      const resData = response.data;
      setClientes(resData?.data || []);
      setTotalClientes(resData?.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalClientes / PAGE_SIZE);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      cargarClientes(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleDelete = async (id) => {
    try {
      await receptoresApi.delete(id);
      setClientes(prev => (prev || []).filter(c => c.id !== id));
      setShowDeleteModal(null);
      toast.success('Cliente eliminado');
    } catch (error) {
      toast.error('Error al eliminar cliente');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort clients (ensure clientes is always an array)
  const clientesArray = Array.isArray(clientes) ? clientes : [];
  const sortedClientes = [...clientesArray].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';

    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Build direccion string
  const buildDireccion = (cliente) => {
    const parts = [];

    if (cliente.otras_senas) {
      parts.push(cliente.otras_senas);
    }

    if (cliente.provincia) {
      parts.push(`Ciudad: ${PROVINCIAS[cliente.provincia] || cliente.provincia}`);
    }

    if (cliente.telefono_numero) {
      parts.push(`Teléfono: (${cliente.telefono_codigo || '506'}) ${cliente.telefono_numero}`);
    }

    if (cliente.identificacion) {
      const tipoLabel = cliente.tipo_identificacion === '02' ? 'Ced.Jurídica' : 'Ced.Física';
      parts.push(`${tipoLabel}: ${cliente.tipo_identificacion}-${cliente.identificacion}`);
    }

    return parts.join('\n');
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ?
      <ChevronUp className="h-4 w-4 inline" /> :
      <ChevronDown className="h-4 w-4 inline" />;
  };

  return (
    <div className="space-y-4">
      {/* Header: Search + Toolbar */}
      <div className="bg-white rounded-lg border p-3 relative">
        {/* Search - left */}
        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o identificación..."
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-80 text-sm"
            />
          </div>
          <span className="text-sm text-gray-500">{totalClientes} reg.</span>
        </div>
        {/* Toolbar - center of full width */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <ToolbarButton
            icon={Plus}
            label="Nuevo Cliente"
            onClick={() => navigate('/clientes/nuevo')}
            variant="primary"
          />
          <ToolbarButton
            icon={Upload}
            label="Importar"
            onClick={() => navigate('/clientes/importar')}
          />
          <ToolbarButton
            icon={RefreshCw}
            label="Actualizar"
            onClick={() => cargarClientes(searchTerm)}
            disabled={loading}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            Cargando clientes...
          </div>
        ) : clientesArray.length === 0 ? (
          <div className="p-12 text-center">
            <User className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay clientes</h3>
            <p className="text-gray-500 mb-4">Comience agregando su primer cliente</p>
            <button
              onClick={() => navigate('/clientes/nuevo')}
              className="inline-flex items-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              <Plus className="h-4 w-4" />
              Agregar Cliente
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th
                    className="text-left px-4 py-3 font-medium text-gray-700 cursor-pointer hover:bg-gray-100 w-80"
                    onClick={() => handleSort('nombre')}
                  >
                    Nombre <SortIcon field="nombre" />
                  </th>
                  <th
                    className="text-left px-4 py-3 font-medium text-gray-700 cursor-pointer hover:bg-gray-100 w-36"
                    onClick={() => handleSort('identificacion')}
                  >
                    Identificación <SortIcon field="identificacion" />
                  </th>
                  <th
                    className="text-left px-4 py-3 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('email')}
                  >
                    Email <SortIcon field="email" />
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">
                    Teléfono
                  </th>
                  <th className="text-left px-4 py-3 font-medium text-gray-700">
                    Dirección
                  </th>
                  <th
                    className="text-left px-4 py-3 font-medium text-gray-700 cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('actividad_economica')}
                  >
                    Act. Económica <SortIcon field="actividad_economica" />
                  </th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700 w-24">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedClientes.map((cliente) => (
                  <tr
                    key={cliente.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {/* Nombre */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {cliente.nombre}
                      </div>
                      {cliente.nombre_comercial && (
                        <div className="text-xs text-gray-500">
                          {cliente.nombre_comercial}
                        </div>
                      )}
                    </td>

                    {/* Identificación */}
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {cliente.identificacion}
                      </span>
                      <div className="text-xs text-gray-400 mt-1">
                        {TIPOS_IDENTIFICACION[cliente.tipo_identificacion]}
                      </div>
                    </td>

                    {/* Email */}
                    <td className="px-4 py-3">
                      {cliente.email ? (
                        <a
                          href={`mailto:${cliente.email}`}
                          className="text-blue-600 hover:underline"
                        >
                          {cliente.email}
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* Teléfono */}
                    <td className="px-4 py-3">
                      {cliente.telefono_numero ? (
                        <a
                          href={`tel:+${cliente.telefono_codigo || '506'}${cliente.telefono_numero}`}
                          className="text-gray-700 hover:text-blue-600"
                        >
                          +{cliente.telefono_codigo || '506'} {cliente.telefono_numero}
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* Dirección */}
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-600 max-w-xs">
                        {cliente.otras_senas ? (
                          <div className="truncate" title={cliente.otras_senas}>{cliente.otras_senas}</div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                        {cliente.provincia && (
                          <div className="text-gray-400">{PROVINCIAS[cliente.provincia]}</div>
                        )}
                      </div>
                    </td>

                    {/* Actividad Económica */}
                    <td className="px-4 py-3">
                      {cliente.actividad_economica ? (
                        <div>
                          <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-mono">
                            {cliente.actividad_economica}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* Acciones */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => navigate(`/clientes/${cliente.id}/editar`)}
                          className="p-1.5 hover:bg-blue-50 rounded transition-colors"
                          title="Editar"
                        >
                          <Pencil className="h-5 w-5 text-blue-500" />
                        </button>
                        <button
                          onClick={() => setShowDeleteModal(cliente)}
                          className="p-1.5 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Delete className="h-5 w-5 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            Mostrando {((page - 1) * PAGE_SIZE) + 1} - {Math.min(page * PAGE_SIZE, totalClientes)} de {totalClientes}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .map((p, idx, arr) => (
                <span key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && <span className="text-gray-400 mx-1">...</span>}
                  <button
                    onClick={() => setPage(p)}
                    className={`px-3 py-1.5 rounded-lg text-sm ${p === page ? 'bg-blue-600 text-white' : 'border border-gray-300 hover:bg-gray-50'}`}
                  >
                    {p}
                  </button>
                </span>
              ))
            }
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Eliminar Cliente</h2>
            <p className="text-gray-600 mb-4">
              ¿Está seguro que desea eliminar a <strong>{showDeleteModal.nombre}</strong>?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
